import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { PriorityBadge, StatusBadge } from "../components/Badges";
import { STATUSES, STATUS_ORDER, PRIORITIES, fmtDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { drawLetterhead, drawFooterWave, contentBottom, BRAND_RED_HEX } from "../lib/docTemplate";
import { LOGO_DATA_URL } from "../lib/logoData";
import { FileBarChart, Download, Plus, Trash2, Rocket, Layers, Save, FolderPlus, FileText } from "lucide-react";

const ESTATUS_SUGG = ["Liberado", "Pendiente", "Trabajando", "En revisión", "Generación de json"];
const TAG_SUGG = ["Mejora continua", "Nuevo"];
const RED_RGB = [232, 105, 91];

const parsePct = (v) => { const m = String(v ?? "").match(/-?\d+/); return m ? parseInt(m[0], 10) : null; };
const avanceRGB = (p) => (p == null ? [100, 116, 139] : p >= 80 ? [22, 163, 74] : p >= 40 ? [202, 138, 4] : [220, 38, 38]);
const avanceTextClass = (v) => {
  const p = parsePct(v);
  if (p == null) return "text-slate-400";
  if (p >= 80) return "text-green-600";
  if (p >= 40) return "text-amber-500";
  return "text-red-600";
};
const AV_COLS = [
  { key: "nombre", label: "Nombre", w: "min-w-[280px]" },
  { key: "estatus", label: "Estatus", list: "estatus-list" },
  { key: "tag", label: "Tag", list: "tag-list" },
  { key: "avance", label: "Avance Semanal", w: "w-40" },
];

function downloadCSV(filename, lines2d) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = "\uFEFF" + lines2d.map((row) => row.map(esc).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

const uid = () => `id-${Math.random().toString(36).slice(2, 10)}`;

export default function ReportsPage() {
  const { isPM } = useAuth();
  const [sections, setSections] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [devs, setDevs] = useState([]);
  const [desde, setDesde] = useState(todayISO(-30));
  const [hasta, setHasta] = useState(todayISO(0));

  useEffect(() => {
    if (!isPM) return;
    api.get("/reports/avances").then((r) => { setSections(r.data.sections || []); setSavedAt(r.data.updated_at); });
    api.get("/developments").then((r) => setDevs(r.data));
  }, [isPM]);

  const setCell = (si, ri, key, val) => setSections((prev) => prev.map((s, i) => (i !== si ? s : { ...s, rows: s.rows.map((row, j) => (j !== ri ? row : { ...row, [key]: val })) })));
  const setTitle = (si, val) => setSections((prev) => prev.map((s, i) => (i === si ? { ...s, title: val } : s)));
  const addRow = (si) => setSections((prev) => prev.map((s, i) => (i === si ? { ...s, rows: [...s.rows, { id: uid(), nombre: "", estatus: "", tag: "", avance: "" }] } : s)));
  const delRow = (si, ri) => setSections((prev) => prev.map((s, i) => (i === si ? { ...s, rows: s.rows.filter((_, j) => j !== ri) } : s)));
  const delSection = (si) => setSections((prev) => prev.filter((_, i) => i !== si));
  const addSection = () => setSections((prev) => [...prev, { id: uid(), title: "Nueva categoría", rows: [{ id: uid(), nombre: "", estatus: "", tag: "", avance: "" }] }]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/reports/avances", { sections });
      setSavedAt(data.updated_at);
      toast.success("Reporte guardado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const prodDate = (d) => d.produccion_at || d.updated_at;
  const enProduccion = useMemo(() => {
    const from = desde ? new Date(desde + "T00:00:00") : null;
    const to = hasta ? new Date(hasta + "T23:59:59") : null;
    return devs
      .filter((d) => d.status === "en_produccion")
      .filter((d) => { const pd = prodDate(d) ? new Date(prodDate(d)) : null; if (!pd) return false; if (from && pd < from) return false; if (to && pd > to) return false; return true; })
      .sort((a, b) => new Date(prodDate(b)) - new Date(prodDate(a)));
  }, [devs, desde, hasta]);

  const grouped = useMemo(() => (
    STATUS_ORDER.map((s) => ({ key: s, label: STATUSES[s].label, items: devs.filter((d) => d.status === s) })).filter((g) => g.items.length > 0)
  ), [devs]);

  const exportProduccion = () => downloadCSV(`produccion_${desde}_a_${hasta}.csv`, [
    ["ID", "Proyecto", "Sistema", "Módulo", "Responsables", "Fecha producción"],
    ...enProduccion.map((d) => [d.dev_id, d.title, d.sistema, d.modulo, (d.responsables || []).map((r) => r.name).join(", "), fmtDate(prodDate(d))]),
  ]);
  const exportGrouped = () => downloadCSV(`desarrollos_por_estado_${todayISO()}.csv`, [
    ["Estado", "ID", "Proyecto", "Prioridad", "Avance %", "Responsables", "Fecha compromiso"],
    ...grouped.flatMap((g) => g.items.map((d) => [g.label, d.dev_id, d.title, PRIORITIES[d.priority]?.label, d.avance, (d.responsables || []).map((r) => r.name).join(", "), d.fecha_compromiso ? fmtDate(d.fecha_compromiso) : ""])),
  ]);

  const avanceHex = (v) => {
    const p = parsePct(v);
    if (p == null) return "FF64748B";
    if (p >= 80) return "FF16A34A";
    if (p >= 40) return "FFCA8A04";
    return "FFDC2626";
  };

  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Reporte de Avances", { views: [{ showGridLines: false }] });
    ws.columns = [{ width: 46 }, { width: 22 }, { width: 24 }, { width: 18 }];

    const RED = "FFE8695B";
    const DARK = BRAND_RED_HEX;
    const SLATE = "FF334155";

    // Encabezado con logo (membrete Gerencia TI)
    const imgId = wb.addImage({ base64: LOGO_DATA_URL.split(",")[1], extension: "png" });
    ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: 60, height: 46 } });
    ws.mergeCells("A1:D1");
    const titleCell = ws.getCell("A1");
    titleCell.value = "Dirección de TI  ·  Reporte de Avances";
    titleCell.font = { bold: true, size: 18, color: { argb: "FFFFFFFF" } };
    titleCell.alignment = { vertical: "middle", horizontal: "left", indent: 9 };
    ws.getRow(1).height = 40;
    ws.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
    for (const c of ["B1", "C1", "D1"]) ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
    ws.mergeCells("A2:D2");
    ws.getCell("A2").value = "Comprometidos con la innovación, la eficiencia y el crecimiento.  ·  " + new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
    ws.getCell("A2").font = { color: { argb: "FF94A3B8" }, size: 10 };
    ws.getCell("A2").alignment = { vertical: "middle", horizontal: "left", indent: 9 };
    ws.getCell("A2").fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
    for (const c of ["B2", "C2", "D2"]) ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
    for (const c of ["B2", "C2", "D2"]) ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
    ws.addRow([]);

    const border = { top: { style: "thin", color: { argb: "FFE2E8F0" } }, left: { style: "thin", color: { argb: "FFE2E8F0" } }, bottom: { style: "thin", color: { argb: "FFE2E8F0" } }, right: { style: "thin", color: { argb: "FFE2E8F0" } } };

    const headerRow = (cells, fill) => {
      const row = ws.addRow(cells);
      row.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.border = border;
      });
      return row;
    };

    // Secciones de avances (estilo imagen)
    sections.forEach((s) => {
      headerRow([s.title, "Estatus", "Tag", "Avance Semanal"], RED);
      s.rows.forEach((r) => {
        const row = ws.addRow([r.nombre, r.estatus, r.tag, r.avance]);
        row.eachCell((cell) => { cell.border = border; });
        const av = row.getCell(4);
        av.alignment = { horizontal: "center" };
        av.font = { bold: true, color: { argb: avanceHex(r.avance) } };
      });
      ws.addRow([]);
    });

    // En Producción
    headerRow([`En Producción (${desde} a ${hasta})`, "Sistema / Módulo", "Responsables", "Fecha producción"], SLATE);
    if (enProduccion.length) {
      enProduccion.forEach((d) => {
        const row = ws.addRow([`${d.dev_id}  ${d.title}`, [d.sistema, d.modulo].filter(Boolean).join(" / "), (d.responsables || []).map((r) => r.name).join(", "), fmtDate(prodDate(d))]);
        row.eachCell((cell) => { cell.border = border; });
      });
    } else {
      ws.addRow(["Sin desarrollos en producción en ese rango"]);
    }
    ws.addRow([]);

    // Desarrollos por estado
    headerRow(["Desarrollos por estado", "Prioridad", "Responsables", "Avance"], SLATE);
    grouped.forEach((g) => {
      const gr = ws.addRow([g.label]);
      gr.getCell(1).font = { bold: true, italic: true, color: { argb: SLATE } };
      g.items.forEach((d) => {
        const row = ws.addRow([`${d.dev_id}  ${d.title}`, PRIORITIES[d.priority]?.label, (d.responsables || []).map((r) => r.name).join(", "), `${d.avance}%`]);
        row.eachCell((cell) => { cell.border = border; });
        const av = row.getCell(4);
        av.alignment = { horizontal: "center" };
        av.font = { bold: true, color: { argb: avanceHex(`${d.avance}%`) } };
      });
    });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `reporte_avances_${todayISO()}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };


  const exportPDF = async () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const stamp = () => { drawLetterhead(doc, { title: "Reporte de Avances" }); drawFooterWave(doc); };
    const contentTop = drawLetterhead(doc, { title: "Reporte de Avances" });
    drawFooterWave(doc);
    const bottom = contentBottom(doc);

    const colorAvance = (data, colIndex) => {
      if (data.section === "body" && data.column.index === colIndex) {
        data.cell.styles.textColor = avanceRGB(parsePct(data.cell.raw));
        data.cell.styles.fontStyle = "bold";
      }
    };

    let y = contentTop;
    sections.forEach((s) => {
      autoTable(doc, {
        startY: y,
        head: [[s.title, "Estatus", "Tag", "Avance Semanal"]],
        body: s.rows.map((r) => [r.nombre, r.estatus, r.tag, r.avance]),
        headStyles: { fillColor: RED_RGB, textColor: 255, fontStyle: "bold" },
        styles: { fontSize: 8, cellPadding: 3, lineColor: [226, 232, 240], lineWidth: 0.5 },
        columnStyles: { 3: { halign: "center", cellWidth: 90 } },
        didParseCell: (d) => colorAvance(d, 3),
        didDrawPage: stamp,
        margin: { left: 40, right: 40, top: contentTop, bottom: doc.internal.pageSize.getHeight() - bottom },
      });
      y = doc.lastAutoTable.finalY + 14;
    });

    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(30);
    if (y > bottom - 40) { doc.addPage(); stamp(); y = contentTop; }
    doc.text(`En Producción (${desde} a ${hasta})`, 40, y); y += 6;
    autoTable(doc, {
      startY: y,
      head: [["ID", "Proyecto", "Sistema / Módulo", "Responsables", "Fecha producción"]],
      body: enProduccion.length ? enProduccion.map((d) => [d.dev_id, d.title, [d.sistema, d.modulo].filter(Boolean).join(" / "), (d.responsables || []).map((r) => r.name).join(", "), fmtDate(prodDate(d))]) : [["", "Sin desarrollos en producción en ese rango", "", "", ""]],
      headStyles: { fillColor: [51, 65, 85], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 3 },
      didDrawPage: stamp,
      margin: { left: 40, right: 40, top: contentTop, bottom: doc.internal.pageSize.getHeight() - bottom },
    });
    y = doc.lastAutoTable.finalY + 16;

    if (y > bottom - 40) { doc.addPage(); stamp(); y = contentTop; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(30);
    doc.text("Desarrollos por estado", 40, y); y += 6;
    autoTable(doc, {
      startY: y,
      head: [["Estado", "ID", "Proyecto", "Prioridad", "Avance", "Responsables", "Compromiso"]],
      body: grouped.flatMap((g) => g.items.map((d) => [g.label, d.dev_id, d.title, PRIORITIES[d.priority]?.label, `${d.avance}%`, (d.responsables || []).map((r) => r.name).join(", "), d.fecha_compromiso ? fmtDate(d.fecha_compromiso) : "—"])),
      headStyles: { fillColor: [51, 65, 85], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 4: { halign: "center" } },
      didParseCell: (d) => colorAvance(d, 4),
      didDrawPage: stamp,
      margin: { left: 40, right: 40, top: contentTop, bottom: doc.internal.pageSize.getHeight() - bottom },
    });

    doc.save(`reporte_avances_${todayISO()}.pdf`);
  };

  if (!isPM) return <div className="p-8 text-slate-400">Este reporte solo está disponible para el PM.</div>;

  return (
    <div>
      <datalist id="estatus-list">{ESTATUS_SUGG.map((v) => <option key={v} value={v} />)}</datalist>
      <datalist id="tag-list">{TAG_SUGG.map((v) => <option key={v} value={v} />)}</datalist>

      <div className="bg-white border-b border-border px-8 py-6 flex items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Reportes</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2">
            <FileBarChart className="h-7 w-7" /> Reporte de Avances
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-[11px] text-slate-400">Guardado: {new Date(savedAt).toLocaleString("es-MX")}</span>}
          <Button variant="outline" className="rounded-sm h-9 border-slate-300" onClick={exportExcel} data-testid="report-export-avances-btn"><Download className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" className="rounded-sm h-9 border-slate-300" onClick={exportPDF} data-testid="report-export-pdf-btn"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button className="rounded-sm h-9 bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving} data-testid="report-save-btn"><Save className="h-4 w-4 mr-1.5" />{saving ? "Guardando..." : "Guardar"}</Button>
        </div>
      </div>

      <div className="p-8 space-y-10">
        {/* Reporte de avances por secciones */}
        <section data-testid="report-avances" className="space-y-6">
          {sections.map((s, si) => (
            <div key={s.id} data-testid={`avance-section-${si}`} className="border border-border rounded-sm overflow-hidden">
              <div className="flex items-center gap-2 bg-[#e8695b] px-3 py-2">
                <input value={s.title} onChange={(e) => setTitle(si, e.target.value)} data-testid={`section-title-${si}`}
                  className="flex-1 bg-transparent text-white font-bold tracking-tight placeholder-white/60 focus:outline-none" />
                <button onClick={() => addRow(si)} title="Agregar fila" data-testid={`section-add-row-${si}`} className="text-white/90 hover:text-white p-1"><Plus className="h-4 w-4" /></button>
                <button onClick={() => delSection(si)} title="Eliminar categoría" data-testid={`section-delete-${si}`} className="text-white/90 hover:text-white p-1"><Trash2 className="h-4 w-4" /></button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-border">
                    {AV_COLS.map((c) => <th key={c.key} className={`text-left text-[11px] font-bold uppercase tracking-wide text-slate-600 px-2 py-1.5 ${c.w || ""}`}>{c.label}</th>)}
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {s.rows.map((r, ri) => (
                    <tr key={r.id} data-testid={`avance-row-${si}-${ri}`} className="border-b border-slate-100 last:border-0">
                      {AV_COLS.map((c) => (
                        <td key={c.key} className="px-1 py-0.5">
                          <input value={r[c.key] ?? ""} list={c.list} onChange={(e) => setCell(si, ri, c.key, e.target.value)}
                            className={`w-full bg-transparent px-1.5 py-1 rounded-sm text-sm focus:outline-none focus:bg-slate-50 focus:ring-1 focus:ring-slate-300 ${c.key === "avance" ? avanceTextClass(r[c.key]) + " font-bold text-center" : ""}`} />
                        </td>
                      ))}
                      <td className="px-1"><button onClick={() => delRow(si, ri)} data-testid={`avance-del-row-${si}-${ri}`} className="text-slate-300 hover:text-red-600 transition-colors duration-200"><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                  {s.rows.length === 0 && <tr><td colSpan={AV_COLS.length + 1} className="text-center text-slate-400 py-4 text-xs">Sin filas.</td></tr>}
                </tbody>
              </table>
            </div>
          ))}
          <Button variant="outline" className="rounded-sm h-9 border-slate-300 border-dashed" onClick={addSection} data-testid="report-add-section-btn"><FolderPlus className="h-4 w-4 mr-1.5" />Agregar categoría</Button>
        </section>

        {/* Desarrollos en producción por rango de fechas */}
        <section data-testid="report-produccion">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
            <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
              <Rocket className="h-5 w-5 text-slate-500" /> En Producción
              <span className="font-mono text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm">{enProduccion.length}</span>
            </h2>
            <div className="flex items-end gap-2">
              <div><Label className="text-[11px] uppercase tracking-wider text-slate-500">Desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="rounded-sm h-9 w-40" data-testid="report-desde" /></div>
              <div><Label className="text-[11px] uppercase tracking-wider text-slate-500">Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="rounded-sm h-9 w-40" data-testid="report-hasta" /></div>
              <Button variant="outline" className="rounded-sm h-9 border-slate-300" onClick={exportProduccion} data-testid="report-export-prod-btn"><Download className="h-4 w-4 mr-1.5" />Exportar</Button>
            </div>
          </div>
          <div className="border border-border bg-white rounded-sm divide-y divide-border">
            {enProduccion.length === 0 && <div className="px-4 py-8 text-center text-slate-400 text-sm">No hay desarrollos en producción en ese rango.</div>}
            {enProduccion.map((d) => (
              <div key={d.id} data-testid={`prod-${d.dev_id}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className="font-mono text-[10px] text-slate-400 w-16">{d.dev_id}</span>
                <span className="flex-1 min-w-0 font-medium truncate">{d.title}</span>
                <span className="text-xs text-slate-500 truncate max-w-[200px]">{[d.sistema, d.modulo].filter(Boolean).join(" › ")}</span>
                <span className="text-xs text-slate-500 truncate max-w-[160px]">{(d.responsables || []).map((r) => r.name).join(", ")}</span>
                <span className="font-mono text-xs text-slate-600 w-24 text-right">{fmtDate(prodDate(d))}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Desarrollos agrupados por estado */}
        <section data-testid="report-grouped">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h2 className="text-lg font-bold tracking-tight flex items-center gap-2"><Layers className="h-5 w-5 text-slate-500" /> Desarrollos por estado</h2>
            <Button variant="outline" className="rounded-sm h-9 border-slate-300" onClick={exportGrouped} data-testid="report-export-grouped-btn"><Download className="h-4 w-4 mr-1.5" />Exportar</Button>
          </div>
          <div className="space-y-5">
            {grouped.map((g) => (
              <div key={g.key} data-testid={`report-group-${g.key}`}>
                <div className="flex items-center gap-2 mb-2"><StatusBadge status={g.key} /><span className="font-mono text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm">{g.items.length}</span></div>
                <div className="border border-border bg-white rounded-sm divide-y divide-border">
                  {g.items.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 px-4 py-2.5">
                      <span className="font-mono text-[10px] text-slate-400 w-16">{d.dev_id}</span>
                      <span className="flex-1 min-w-0 font-medium truncate">{d.title}</span>
                      <PriorityBadge priority={d.priority} />
                      <span className="text-xs text-slate-500 truncate max-w-[160px]">{(d.responsables || []).map((r) => r.name).join(", ")}</span>
                      <span className={`font-mono text-xs w-12 text-right font-bold ${avanceTextClass(d.avance)}`}>{d.avance}%</span>
                      <span className="font-mono text-xs text-slate-500 w-24 text-right">{d.fecha_compromiso ? fmtDate(d.fecha_compromiso) : "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {grouped.length === 0 && <p className="text-slate-400 text-sm">Sin desarrollos.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}
